// @flow
import { useEffect, useState } from "react";

import { HTTP_METHODS } from "../constants";

import Href from "../models/href/Href";
import LinkModel from "../models/links/LinkModel";
import LookupOptionsModel from "../models/lookup/LookupOptionsModel";

import { useModularUIRequest } from "./useModularUIRequest";
import { useProgressIndicator } from "./useProgressIndicator";

type UseLookupHook = {
  lookupOptions: LookupOptionsModel | null,
  lookupLoading: boolean,
};

/**
 * Lookup options call
 */
export const useLookup = (
  lookupLink: LinkModel,
  filterInput: string = "",
  minLength: number = 1,
  debounceMs: number = 300,
): UseLookupHook => {
  const { start, finish } = useProgressIndicator();
  const modularuiRequest = useModularUIRequest();

  const [lookupOptions, setLookupOptions] = useState(null);
  const [lookupLoading, setLookupLoading] = useState(false);

  const { href, filterName, method } = lookupLink;

  useEffect(() => {
    if (filterInput.length < minLength) {
      setLookupOptions(null);
      return;
    }

    const timeoutId = setTimeout(async () => {
      setLookupLoading(true);
      start();

      const lookupHref = new Href(href);
      const requestOptions = { method, data: undefined };
      if (method === HTTP_METHODS.POST) {
        requestOptions.data = { [filterName]: filterInput };
      } else {
        lookupHref.addParameter(filterName, filterInput);
      }

      modularuiRequest(lookupHref, requestOptions)
        .fetch()
        .then((lookupResponse) => {
          if (lookupResponse instanceof LookupOptionsModel) {
            setLookupOptions(lookupResponse);
          }
        })
        .finally(() => {
          finish();
          setLookupLoading(false);
        });
    }, debounceMs);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [
    debounceMs,
    filterInput,
    filterName,
    finish,
    href,
    method,
    minLength,
    modularuiRequest,
    start,
  ]);

  return { lookupOptions, lookupLoading };
};
